import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

export default function PolitykaCookies() {
  const { t } = useTranslation();
  const handleOpenSettings = () => {
    // Clear cookie consent to show banner again
    localStorage.removeItem('apisai_cookie_consent');
    window.location.reload();
  };

  return (
    <>
      <Helmet>
        <title>Polityka cookies | ApisAI</title>
        <meta name="description" content="Polityka cookies serwisu ApisAI - jak używamy plików cookies." />
      </Helmet>

      <div className="min-h-screen bg-[#0B0F1A]">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/5">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <span className="font-semibold text-white">Polityka cookies</span>
          </div>
        </nav>

        {/* Content */}
        <main className="pt-24 pb-16 px-6">
          <div className="max-w-3xl mx-auto prose prose-invert prose-gray">
            <h1 className="text-3xl font-bold text-white mb-8">Polityka cookies</h1>
            
            <p className="text-gray-400 mb-6">Ostatnia aktualizacja: 24 stycznia 2026</p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">Czym są pliki cookies?</h2>
              <p className="text-gray-300">
                Cookies (ciasteczka) to małe pliki tekstowe zapisywane na Twoim urządzeniu przez przeglądarkę. 
                Służą do zapamiętywania preferencji, utrzymywania sesji logowania i analizy ruchu na stronie.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">Jakie cookies używamy?</h2>
              
              <div className="space-y-6">
                {/* Necessary */}
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                      <Cookie className="w-4 h-4 text-green-400" />
                    </div>
                    <h3 className="font-semibold text-white">Niezbędne</h3>
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">Zawsze aktywne</span>
                  </div>
                  <p className="text-gray-400 text-sm mb-3">
                    Konieczne do działania serwisu. Nie można ich wyłączyć.
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-400">
                        <th className="text-left py-1">Nazwa</th>
                        <th className="text-left py-1">Cel</th>
                        <th className="text-left py-1">Ważność</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-300">
                      <tr>
                        <td className="py-1">token</td>
                        <td className="py-1">Sesja logowania</td>
                        <td className="py-1">Sesja</td>
                      </tr>
                      <tr>
                        <td className="py-1">apisai_cookie_consent</td>
                        <td className="py-1">Zgoda na cookies</td>
                        <td className="py-1">1 rok</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Analytics */}
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Cookie className="w-4 h-4 text-blue-400" />
                    </div>
                    <h3 className="font-semibold text-white">Analityczne</h3>
                    <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 text-xs rounded-full">Opcjonalne</span>
                  </div>
                  <p className="text-gray-400 text-sm mb-3">
                    Pomagają nam zrozumieć, jak użytkownicy korzystają z serwisu.
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-400">
                        <th className="text-left py-1">Nazwa</th>
                        <th className="text-left py-1">Dostawca</th>
                        <th className="text-left py-1">Ważność</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-300">
                      <tr>
                        <td className="py-1">_ga</td>
                        <td className="py-1">Google Analytics</td>
                        <td className="py-1">2 lata</td>
                      </tr>
                      <tr>
                        <td className="py-1">_gid</td>
                        <td className="py-1">Google Analytics</td>
                        <td className="py-1">24h</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Marketing */}
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
                      <Cookie className="w-4 h-4 text-amber-400" />
                    </div>
                    <h3 className="font-semibold text-white">Marketingowe</h3>
                    <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 text-xs rounded-full">Opcjonalne</span>
                  </div>
                  <p className="text-gray-400 text-sm mb-3">
                    Używane do personalizacji reklam i mierzenia ich skuteczności.
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-400">
                        <th className="text-left py-1">Nazwa</th>
                        <th className="text-left py-1">Dostawca</th>
                        <th className="text-left py-1">Ważność</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-300">
                      <tr>
                        <td className="py-1">_fbp</td>
                        <td className="py-1">Meta (Facebook)</td>
                        <td className="py-1">3 miesiące</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">Jak zarządzać cookies?</h2>
              <p className="text-gray-300 mb-4">
                Możesz zmienić swoje preferencje dotyczące cookies w dowolnym momencie:
              </p>
              <Button 
                onClick={handleOpenSettings}
                className="bg-blue-500 hover:bg-blue-600 rounded-full"
              >
                <Cookie className="w-4 h-4 mr-2" />
                Zmień ustawienia cookies
              </Button>
              <p className="text-gray-400 text-sm mt-4">
                Możesz również zarządzać cookies w ustawieniach przeglądarki. 
                Pamiętaj, że wyłączenie niektórych cookies może wpłynąć na działanie serwisu.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">Linki do ustawień przeglądarek</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/pl/kb/ciasteczka" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/pl-pl/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Safari</a></li>
                <li><a href="https://support.microsoft.com/pl-pl/microsoft-edge/usuwanie-plik%C3%B3w-cookie-w-przegl%C4%85darce-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Microsoft Edge</a></li>
              </ul>
            </section>

            <div className="mt-12 pt-8 border-t border-white/10">
              <p className="text-gray-500 text-sm">
                Kontakt: kontakt@apisai.pl
              </p>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-white/10 py-8">
          <div className="max-w-4xl mx-auto px-6 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
            <Link to="/regulamin" className="hover:text-white transition-colors">Regulamin</Link>
            <Link to="/polityka-prywatnosci" className="hover:text-white transition-colors">Polityka prywatności</Link>
            <Link to="/polityka-cookies" className="hover:text-white transition-colors">Cookies</Link>
            <Link to="/kontakt" className="hover:text-white transition-colors">Kontakt</Link>
          </div>
        </footer>
      </div>
    </>
  );
}
